import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { DoorOpen, Send, Check, ShieldCheck, Heart, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Private waiting list — zero commitment, zero money, zero regulated activity.
 * Captures interest so validated demand isn't lost while projects await
 * legal sign-off and Mavula pursues FCA Payment Institution status.
 */

const COUNTRIES = [
  "Zimbabwe", "Zambia", "Botswana", "Namibia", "South Africa",
  "Eswatini", "Malawi", "Lesotho", "United Kingdom", "Other",
];

const PROJECTS = [
  "Borrowdale Sunrise · Harare",
  "Ibex Hill Residence · Lusaka",
  "Melrose Arch Family Estate · Johannesburg",
  "Gaborone / Francistown estates (coming)",
  "Namibia — coastal & inland (coming)",
  "Not sure yet — send me options",
];

export default function WaitingListPage() {
  const [f, setF] = useState({
    name: "", email: "", country: "", project_of_interest: "",
    whatsapp: "", whatsapp_opt_in: false,
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const on = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email || !f.country) {
      toast.error("Please share your name, email and country.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/waiting-list", f);
      setResult(data);
      toast.success(data?.already_on_list
        ? `Already at #${data.position} — we'll be in touch.`
        : `You're #${data.position} on the list.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong — please try again in a moment.");
    } finally { setBusy(false); }
  };

  if (result) {
    return (
      <main className="bg-white" data-testid="waiting-list-page">
        <section className="mv-green-bg text-white py-20 md:py-28 relative overflow-hidden">
          <div className="absolute inset-0 opacity-15" style={{ background: "radial-gradient(600px 300px at 20% 0%, var(--mv-gold), transparent 60%)" }}/>
          <div className="max-w-3xl mx-auto px-4 md:px-8 relative text-center">
            <div className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 mb-6" style={{ background: "rgba(255,255,255,0.10)", border: "1px solid rgba(212,175,55,0.55)" }}>
              <Check size={14} style={{ color: "var(--mv-gold)" }} />
              <span className="text-[11px] uppercase tracking-[0.28em] text-white font-semibold">You're in</span>
            </div>
            <h1 className="font-display text-4xl md:text-6xl leading-[1.02]" data-testid="wl-success-heading">
              You're <span className="font-accent italic" style={{ color: "var(--mv-gold)" }}>#{result.position}</span> on the list.
            </h1>
            <p className="mt-6 text-white/85 text-[15.5px] leading-relaxed max-w-xl mx-auto">
              {result.already_on_list
                ? "You were already saved. We haven't forgotten — and we won't."
                : "Thank you for trusting us with your interest."} We'll write to you personally as soon as your chosen project completes legal sign-off. <strong>No money changes hands today. No obligation. Ever.</strong>
            </p>
            <div className="mt-10 flex flex-wrap gap-3 justify-center">
              <Link to="/" className="btn-gold" data-testid="wl-back-home">Back to Home</Link>
              <Link to="/about" className="btn-ghost !bg-white/10 !text-white !border-white/30 hover:!bg-white/20" data-testid="wl-read-about">Read About Us</Link>
            </div>
            <div className="mt-12 text-[13px] text-white/70 font-accent italic">
              Please share this page with anyone in your family who's been quietly hoping.
            </div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="bg-white" data-testid="waiting-list-page">
      {/* Hero */}
      <section className="mv-green-bg text-white py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-15" style={{ background: "radial-gradient(600px 300px at 20% 0%, var(--mv-gold), transparent 60%)" }}/>
        <div className="max-w-4xl mx-auto px-4 md:px-8 relative">
          <div className="text-[11px] uppercase tracking-[0.28em]" style={{ color: "var(--mv-gold)" }}>§ Private Waiting List</div>
          <h1 className="font-display text-4xl md:text-6xl mt-4 leading-[1.02]">
            Be the first invited when the <span className="font-accent italic" style={{ color: "var(--mv-gold)" }}>door opens.</span>
          </h1>
          <p className="mt-6 text-white/85 text-[15.5px] leading-relaxed max-w-2xl">
            Mavula is quietly getting each project ready — legally, structurally, honestly. When a project's lawyers give the green light, we invite the people on this list <strong>individually</strong>, by name. You'll never see hype. You'll never be pressured. And you send us nothing until <em>you</em> decide.
          </p>

          <div className="mt-8 flex flex-wrap gap-3 text-[12.5px]">
            {[
              { i: <ShieldCheck size={13}/>, t: "No money today" },
              { i: <Heart size={13}/>,       t: "No obligation ever" },
              { i: <MessageCircle size={13}/>, t: "Personal reply, not spam" },
            ].map((c) => (
              <span key={c.t} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full" style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(212,175,55,0.35)" }}>
                <span style={{ color: "var(--mv-gold)" }}>{c.i}</span>
                <span className="text-white/85">{c.t}</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <form onSubmit={submit} className="mv-card p-6 md:p-8 space-y-5" data-testid="wl-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="block">
                <span className="text-[11.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Your name *</span>
                <input value={f.name} onChange={on("name")} required autoComplete="name"
                       data-testid="wl-name"
                       className="mt-1 w-full px-3 py-2.5 rounded-xl border mv-hairline text-[14.5px]"/>
              </label>
              <label className="block">
                <span className="text-[11.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Email *</span>
                <input type="email" value={f.email} onChange={on("email")} required autoComplete="email"
                       data-testid="wl-email"
                       className="mt-1 w-full px-3 py-2.5 rounded-xl border mv-hairline text-[14.5px]"/>
              </label>
              <label className="block">
                <span className="text-[11.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Country where you want to own *</span>
                <select value={f.country} onChange={on("country")} required
                        data-testid="wl-country"
                        className="mt-1 w-full px-3 py-2.5 rounded-xl border mv-hairline text-[14.5px] bg-white">
                  <option value="">Choose one…</option>
                  {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-[11.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Project of interest</span>
                <select value={f.project_of_interest} onChange={on("project_of_interest")}
                        data-testid="wl-project"
                        className="mt-1 w-full px-3 py-2.5 rounded-xl border mv-hairline text-[14.5px] bg-white">
                  <option value="">Optional — pick one if you know</option>
                  {PROJECTS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </label>
              <label className="block md:col-span-2">
                <span className="text-[11.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>WhatsApp number (optional)</span>
                <input value={f.whatsapp} onChange={on("whatsapp")} placeholder="+44…"
                       autoComplete="tel"
                       data-testid="wl-whatsapp"
                       className="mt-1 w-full px-3 py-2.5 rounded-xl border mv-hairline text-[14.5px]"/>
              </label>
            </div>

            <label className="flex items-start gap-3 cursor-pointer" data-testid="wl-whatsapp-optin-row">
              <input type="checkbox" checked={f.whatsapp_opt_in} onChange={on("whatsapp_opt_in")}
                     data-testid="wl-whatsapp-optin"
                     className="mt-1 w-4 h-4 accent-[color:var(--mv-green)]"/>
              <span className="text-[13px]" style={{ color: "var(--mv-ink)" }}>
                It's ok to send me a short WhatsApp when my project is ready. I can opt out any time.
              </span>
            </label>

            <div className="pt-2 border-t mv-hairline text-[12.5px] leading-relaxed" style={{ color: "var(--mv-muted)" }}>
              This is not a purchase, not a deposit and not an investment. Mavula Investment Ltd (UK Co. 17368822) is not FCA-authorised. Real terms live only in your individual signed project contract with the local developer — and only after independent legal review. <Link to="/legal-status" className="underline mv-green-text">Read our Legal Status</Link>.
            </div>

            <div className="flex flex-wrap justify-between items-center gap-3 pt-2">
              <Link to="/about" className="text-[13px] underline mv-green-text" data-testid="wl-back-to-about">
                What is Mavula, again?
              </Link>
              <button type="submit" disabled={busy}
                      data-testid="wl-submit"
                      className="btn-green disabled:opacity-50 disabled:cursor-not-allowed">
                <Send size={14}/> {busy ? "Adding you…" : "Add me to the list"}
              </button>
            </div>
          </form>

          {/* Reassurance strip */}
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { i: <ShieldCheck size={18}/>, t: "You send us nothing.", d: "No card, no bank details, no deposit. Just your name." },
              { i: <DoorOpen size={18}/>,    t: "You're invited by name.", d: "When your project is ready, we write to you personally — not a mass mail." },
              { i: <Heart size={18}/>,        t: "You can leave anytime.", d: "Reply once with 'remove' and we take you off. Simple as that." },
            ].map((c) => (
              <div key={c.t} className="mv-card p-5">
                <div className="w-9 h-9 rounded-2xl flex items-center justify-center mb-2 mv-green-text" style={{ background: "rgba(212,175,55,0.16)" }}>{c.i}</div>
                <div className="font-display text-[15px] mv-green-text">{c.t}</div>
                <p className="mt-1 text-[13px]" style={{ color: "var(--mv-muted)" }}>{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
